import { SensitiveLogKey } from './error-reporting.types';
import type {
	IErrorReporting,
	IErrorReportingClient,
	IErrorReportingLog,
} from './error-reporting.types';

const SERVER_ERROR_STATUS = 500;

const accountAttributes = (
	user: string
): { user?: string; domain?: string } => {
	const trimmed = user.trim();
	if (trimmed === '') {
		return {};
	}

	const separator = trimmed.indexOf('@');
	const domain = trimmed.slice(separator + 1);
	const canSplit =
		separator > 0 &&
		separator === trimmed.lastIndexOf('@') &&
		domain !== '' &&
		!trimmed.slice(0, separator).includes(' ') &&
		!domain.includes(' ');

	if (!canSplit) {
		return { user: trimmed };
	}

	return { user: trimmed, domain };
};

const sensitiveLogKeys: readonly string[] = Object.values(SensitiveLogKey);

const isSensitiveLogKey = (key: string): boolean =>
	sensitiveLogKeys.includes(key.toLowerCase());

const withoutSensitiveAttributes = (
	log: IErrorReportingLog
): IErrorReportingLog => {
	if (!log.attributes) {
		return log;
	}

	const attributes = Object.fromEntries(
		Object.entries(log.attributes).filter(([key]) => !isSensitiveLogKey(key))
	);
	return { ...log, attributes };
};

/**
 * Builds a reporting facade around an injected error-reporting client. Each
 * instance keeps its own on/off state; nothing is reported until `init` has
 * been called with a non-empty DSN. Payloads are limited to a flow name, an
 * HTTP status, an action name, and the login user. When that user is an
 * email, the domain after "@" is added beside it. The password never
 * reaches the client.
 */
export function createErrorReporting(
	client: IErrorReportingClient
): IErrorReporting {
	const enabledState = { isEnabled: false };

	return {
		init({ dsn, environment, sensitivePaths = [] }) {
			if (enabledState.isEnabled || dsn === '') {
				return;
			}

			// An empty entry would match every URL, so it is ignored.
			const paths = sensitivePaths.filter((path) => path !== '');
			const isSensitive = (value: string | undefined): boolean =>
				value !== undefined && paths.some((path) => value.includes(path));

			client.init({
				dsn,
				environment,
				sendDefaultPii: false,
				beforeBreadcrumb: (breadcrumb) =>
					isSensitive(breadcrumb.data?.url) || isSensitive(breadcrumb.message)
						? null
						: breadcrumb,
				beforeSendLog: withoutSensitiveAttributes,
			});
			enabledState.isEnabled = true;
		},

		reportRequestFailure({ flow, status, user }) {
			if (!enabledState.isEnabled) {
				return;
			}
			const account = accountAttributes(user);
			client.logger.warn('request failed', { flow, status, ...account });
			if (status >= SERVER_ERROR_STATUS) {
				client.captureMessage('request failed', {
					level: 'error',
					tags: { flow, status: String(status), ...account },
				});
			}
		},

		reportAction({ flow, action, user }) {
			if (!enabledState.isEnabled) {
				return;
			}
			client.logger.info('action', {
				flow,
				action,
				...accountAttributes(user),
			});
		},
	};
}
